import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import {
    Box,
    CircularProgress,
    Typography,
    Button
} from "@mui/material";

import axios from "../api/axios";
import useAxiosFunction from "../hooks/useAxiosFunction";
import { Turnstile } from "@marsidev/react-turnstile";
import HeroBanner from "../components/HeroBanner";
import HMRCChecklistForm from "../components/newStarter/HMRCChecklistForm";
import AuthorityToWorkForm from "../components/newStarter/AuthorityToWorkForm";
import DrivingLicenceForm from "../components/newStarter/DrivingLicenceForm";
import StudentLoanForm from "../components/newStarter/StudentLoanForm";
import SkillsAndCertificatesForm from "../components/newStarter/SkillsAndCertificatesForm";
import HealthAndSafetyForm from "../components/newStarter/HealthAndSafetyForm";
import EmergencyInformationForm from "../components/newStarter/EmergencyInformationForm";
import BankDetailsForm from "../components/newStarter/BankDetailsForm";
import DeclarationForm from "../components/newStarter/DeclarationForm";
import SubmissionSuccess from "../components/newStarter/SubmissionSuccess";
import hero2 from '../assets/hero2.jpg';
import btnStyles from '../styles/buttons.module.scss';
import ProfileBanner from "../components/ProfileBanner";
import profile from "../assets/profile.jpg";
import FirstDayDetailsBanner from "../components/FirstDayDetailsBanner";

import WelcomeSection from '../components/newStarter/WelcomeSection';
import FormSection from "../components/newStarter/FormSection";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------

export default function NewStarter() {


    // --------------------------------------------------------
    // Routing
    // --------------------------------------------------------

    const [searchParams] = useSearchParams();

    const navigate = useNavigate();

    const [submitResponse, submitError, axiosLoading, axiosFetch] = useAxiosFunction();

    // --------------------------------------------------------
    // Page State
    // --------------------------------------------------------

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");

    const [starterData, setStarterData] = useState(null);

    const [formStarted, setFormStarted] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    const [submitted, setSubmitted] = useState(false);

    const [turnstileToken, setTurnstileToken] = useState(null);

    const [turnstileError, setTurnstileError] = useState("");

    const [submissionSuccessReturn, setSubmissionSuccessReturn] = useState(false);
    
    // ------------------------------------------------------------
    // New Starter Workflow Sections
    // ------------------------------------------------------------
    const sections = useMemo(() => {
        console.log("starterData: "+starterData)
        const workflow = [
            "HMRC_CHECKLIST",
            "AUTHORITY_TO_WORK",
            "DRIVING_LICENCE",
            "STUDENT_LOAN",
            "SKILLS_AND_CERTIFICATES"
        ];

        if (starterData?.paymentFrequency === "Monthly") {
            workflow.push("HEALTH_AND_SAFETY");
        }

        workflow.push(
            "EMERGENCY_CONTACT",
            "BANK_DETAILS",
            "DECLARATION"
        );

        return workflow;

    }, [starterData]);

    const steps = useMemo(() => {

        const sections = [
            "HMRC Checklist",
            "Authority To Work",
            "Driving Licence",
            "Student Loan",
            "Skills & Certificates"
        ];

        if (starterData?.paymentFrequency === "Monthly") {
            sections.push("Health & Safety");
        }

        sections.push(
            "Emergency Contact",
            "Bank Details",
            "Declaration"
        );

        return sections;

    }, [starterData]);
    

    // --------------------------------------------------------
    // Form Navigation State
    // --------------------------------------------------------

    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        document.documentElement.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant"
        });
    }, [currentStep]);

    const scrollToTop = () => {
        document.documentElement.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant"
        });
    };

    // --------------------------------------------------------
    // Validation State
    // --------------------------------------------------------
    var email_regex = /^[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,125}[a-zA-Z]{2,63}$/;
    var pc_regex = /^[a-zA-Z]{1,2}[0-9][0-9a-zA-Z]?\s?[0-9][a-zA-Z]{2}$/;
    var eir_regex = /^([AC-FHKNPRTV-Y]{1}[0-9]{2}|D6W)[ ]?[0-9AC-FHKNPRTV-Y]{4}$/;
    var phone_number_regex =/^\+?([0-9]\s*){11,17}$/;
    const [errors, setErrors] = useState({});

    const validateDateOfBirth = (dateOfBirth) => {

        if (!dateOfBirth) {
            return "Date of Birth is required.";
        }

        const dob = new Date(dateOfBirth);

        // Invalid date
        if (isNaN(dob.getTime())) {
            return "Please enter a valid Date of Birth.";
        }

        const today = new Date();

        // Must not be in the future
        if (dob > today) {
            return "Date of Birth cannot be in the future.";
        }

        let age = today.getFullYear() - dob.getFullYear();

        const monthDifference = today.getMonth() - dob.getMonth();

        if (
            monthDifference < 0 ||
            (monthDifference === 0 && today.getDate() < dob.getDate())
        ) {
            age--;
        }

        if (age < 18) {
            return "You must be at least 18 years old.";
        }

        if (age > 100) {
            return "Date of Birth cannot be more than 100 years ago.";
        }

        return null;

    };

    const validateHMRCChecklist = () => {

        const errors = {};
        const hmrc = newStarter.hmrcChecklist;
        const nationalInsuranceRegex = /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/;

        const niNumber = hmrc.nationalInsurance
            ?.replace(/\s/g, "")
            .toUpperCase();

        if (!hmrc.firstName.trim()) {
            errors.firstName = "First Name is required.";
        }

        if (!hmrc.lastName.trim()) {
            errors.lastName = "Last Name is required.";
        }

        if (!hmrc.gender) {
            errors.gender = "Please select your gender.";
        }

        const dateOfBirthError = validateDateOfBirth(hmrc.dateOfBirth);

        if (dateOfBirthError) {
            errors.dateOfBirth = dateOfBirthError;
        }

        if (!hmrc.addressLine1.trim()) {
            errors.addressLine1 = "Address Line 1 is required.";
        }

        if (!hmrc.townCity.trim()) {
            errors.townCity = "Town / City is required.";
        }

        if (!hmrc.county.trim()) {
            errors.county = "County is required.";
        }

        if (!hmrc.postcode.trim()) {
            errors.postcode = "Postcode is required.";
        }
        else if ((!pc_regex.test(hmrc.postcode.trim()) && !eir_regex.test(hmrc.postcode.trim()))) {
            errors.postcode = "Please enter a valid postcode.";
        }

        // Optional field - only validate if entered
        if (niNumber && !nationalInsuranceRegex.test(niNumber)) {
            errors.nationalInsurance =
                "Please enter a valid National Insurance number.";
        }

        if (!hmrc.hmrcTaxStatement) {
            errors.hmrcTaxStatement =
                "Please select an HMRC statement.";
        }

        return errors;

    };

    const validatePassportNumber = (passportNumber) => {

        if (!passportNumber?.trim()) {
            return "Passport Number is required.";
        }

        const regex = /^[A-Za-z0-9]{6,20}$/;

        if (!regex.test(passportNumber.trim())) {
            return "Please enter a valid Passport Number.";
        }

        return null;

    };

    const validateVisaExpiryDate = (date) => {

        if (!date) {
            return "Visa expiry date is required.";
        }

        const expiryDate = new Date(date);
        expiryDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (expiryDate < today) {
            return "Visa expiry date cannot be in the past.";
        }

        return null;

    };

    const validateNationality = (nationality) => {

        if (!nationality?.trim()) {
            return "Nationality is required.";
        }

        const regex = /^[A-Za-zÀ-ÿ' -]{2,60}$/;

        if (!regex.test(nationality.trim())) {
            return "Nationality can only contain letters, spaces, hyphens and apostrophes.";
        }

        return null;

    };

    const validateAuthorityToWork = () => {
        const validationErrors = {};
        const authorityToWork = newStarter.authorityToWork;
        const nationalityError = validateNationality(authorityToWork.nationality);

        if (nationalityError) {
            validationErrors.nationality = nationalityError;
        }


        const passportError = validatePassportNumber(authorityToWork.passportNumber);

        if (passportError) {
            validationErrors.passportNumber = passportError;
        }


        if (authorityToWork.requiresVisaWorkPermit !== "true" &&
            authorityToWork.requiresVisaWorkPermit !== "false") {

            validationErrors.requiresVisaWorkPermit =
                "Please select whether you require a Visa / Work Permit.";

        }


        if (
            authorityToWork.requiresVisaWorkPermit === "true"
        ) {


            const visaExpiryError = validateVisaExpiryDate(authorityToWork.visaExpiryDate);

            if (visaExpiryError) {
                validationErrors.visaExpiryDate = visaExpiryError;
            }


            if (!authorityToWork.visaDocument) {

                validationErrors.visaDocument =
                    "Please upload your Visa / Work Permit.";

            }

        }


        return validationErrors;

    };

    const validateDrivingLicence = () => {
        const errors = {};
        const driving = newStarter.drivingLicence;
        if (driving.hasDrivingLicence === "") {

            errors.hasDrivingLicence =
                "Please select Yes or No.";

        }

        if (driving.hasDrivingLicence === "true") {
            if (starterData.paymentFrequency === "Monthly") {
                if (!driving.licenceType) {
                    errors.licenceType = "Please select licence type.";
                }

                if (!driving.datePassed) {
                    errors.datePassed = "Please enter the date passed.";
                }

            }

            if (starterData.paymentFrequency === "Fortnightly") {
                if (!driving.vehicleClass.trim()) {
                    errors.vehicleClass =
                        "Please enter vehicle class.";

                }
            }
            if (driving.endorsements === "") {

                errors.endorsements =
                    "Please select Yes or No.";

            }

            if (
                driving.endorsements === "true" &&
                !driving.endorsementsDetails.trim()
            ) {
                errors.endorsementsDetails =
                    "Please enter the number of points.";
            }

            if (starterData.paymentFrequency === "Monthly" && driving.drivesBusinessMiles === "") {
                errors.drivesBusinessMiles = "Please select Yes or No.";
            }

            if (starterData.paymentFrequency === "Monthly" && driving.drivesBusinessMiles === "true") {
                if (!driving.vehicleInsuranceFile) {

                    errors.vehicleInsuranceFile =
                        "Please upload your Vehicle Insurance.";

                }
            }


        }


        return errors;

    };

    const validateStudentLoan = () => {

        const errors = {};

        const studentLoan = newStarter.studentLoan;


        // ----------------------------------------------------
        // Exemption
        // ----------------------------------------------------

        if (studentLoan.isStudentLoanDeductionExempt === "") {

            errors.isStudentLoanDeductionExempt =
                "Please select Yes or No.";

        }


        // ----------------------------------------------------
        // Loan Plans
        // ----------------------------------------------------

        if (studentLoan.isStudentLoanDeductionExempt === "false") {

            const hasSelectedPlan =

                studentLoan.plan1 ||
                studentLoan.plan2 ||
                studentLoan.plan4 ||
                studentLoan.postgradLoan;


            if (!hasSelectedPlan) {

                errors.loanPlans =
                    "Please select at least one Student Loan plan.";

            }

        }


        return errors;

    };

    const validateBankDetails = () => {

        const errors = {};
        const bank = newStarter.bankDetails;

        const sortCodeRegex = /^\d{2}-\d{2}-\d{2}$/;
        const accountNumberRegex = /^\d{8}$/;

        if (!bank.accountHolderName.trim()) {

            errors.accountHolderName =
                "Account Holder Name is required.";

        }

        if (!bank.bankBuildingSocietyName.trim()) {

            errors.bankBuildingSocietyName =
                "Bank / Building Society is required.";

        }

        if (!bank.branch.trim()) {

            errors.branch =
                "Branch is required.";

        }

        if (!bank.sortCode.trim()) {

            errors.sortCode =
                "Sort Code is required.";

        }
        else if (!sortCodeRegex.test(bank.sortCode)) {

            errors.sortCode =
                "Sort Code must be in the format 12-34-56.";

        }

        if (!bank.accountNumber.trim()) {

            errors.accountNumber =
                "Account Number is required.";

        }
        else if (!accountNumberRegex.test(bank.accountNumber)) {

            errors.accountNumber =
                "Account Number must be 8 digits.";

        }

        return errors;

    };

    const validateDeclaration = () => {

        const errors = {};

        if (!newStarter.declaration.declarationConfirmed) {

            errors.declarationConfirmed =
                "You must confirm the declaration.";

        }

        if (!turnstileToken) {

            errors.turnstile =
                "Please complete the verification.";

        }

        return errors;

    };

    const validateEmergencyInformation = () => {
        const errors = {};

        const emergency = newStarter.emergencyInformation;


        [
            "contact1",
            "contact2"

        ].forEach((contact, index) => {


            if (!emergency[contact].name.trim()) {

                errors[`contact_${index}_name`] =
                    "Name is required.";

            }


            if (!emergency[contact].relationship.trim()) {

                errors[`contact_${index}_relationship`] =
                    "Relationship is required.";

            }


            if (!emergency[contact].telephoneNumber.trim()) {

                errors[`contact_${index}_telephoneNumber`] =
                    "Telephone number is required.";

            }
             if(emergency[contact].telephoneNumber.trim().length > 0 && !phone_number_regex.test(emergency[contact].telephoneNumber.trim())){
            errors[`contact_${index}_telephoneNumber`] = "Please provide a valid contact phone number.";
        }


        });


        return errors;

    };

    const validateEyeTestDate = (date) => {
        if (!date) {
            return "Please enter the latest eye test date.";
        }

        const eyeTestDate = new Date(date);

        if (isNaN(eyeTestDate.getTime())) {
            return "Please enter a valid eye test date.";
        }

        eyeTestDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (eyeTestDate > today) {
            return "The eye test date cannot be in the future.";
        }

        return null;

    };

    const validateSkillsAndCertificates = () => {

        const errors = {};

        const skills = newStarter.skillsAndCertificates;

        if (skills.cscsSkillsCard === "") {
            errors.cscsSkillsCard = "Please select Yes or No.";
        }
        if(skills.paymentFrequency === "Monthly"){
            if (skills.firstAid === "") {
                errors.firstAid = "Please select Yes or No.";
            }

            if (skills.otherTrainingCertificates === "") {
                errors.otherTrainingCertificates = "Please select Yes or No.";
            }
        }

        return errors;

    };

    const validateHealthAndSafety = () => {

        const errors = {};

        const health = newStarter.healthAndSafety;

        if (health.isVduUser === "") {

            errors.isVduUser =
                "Please select Yes or No.";

        }

        if (health.isVduUser === "true") {

            const eyeTestDateError = validateEyeTestDate(health.vduLastEyeTestDate);

            if (eyeTestDateError) {
                errors.vduLastEyeTestDate = eyeTestDateError;
            }

            if (health.vduGlassesIssued === "") {

                errors.vduGlassesIssued =
                    "Please select Yes or No.";

            }

        }

        return errors;

    };

    // --------------------------------------------------------
    // HMRC Checklist Form State
    // --------------------------------------------------------

    const [newStarter, setNewStarter] = useState({

        hmrcChecklist: {

            firstName: "",
            lastName: "",
            gender: "",
            dateOfBirth: "",

            addressLine1: "",
            addressLine2: "",
            townCity: "",
            county: "",
            postcode: "",

            nationalInsurance: "",
            employmentStartDate: "",
            hmrcTaxStatement: ""

        },

        authorityToWork: {

            nationality: "",
            requiresVisaWorkPermit: "",
            visaExpiryDate: "",
            passportNumber: "",
            visaDocument: null

        },

        drivingLicence: {
            hasDrivingLicence: "",
            licenceType: "",
            datePassed: "",
            // Fortnightly only
            vehicleClass: "",
            endorsements: "",
            endorsementsDetails: "",
            // Monthly only
            drivesBusinessMiles: "",
            vehicleInsuranceDocument: null
        },

        studentLoan: {
            isStudentLoanDeductionExempt: "",
            plan1: false,
            plan2: false,
            plan4: false,
            postgradLoan: false
        },

        bankDetails: {
            accountHolderName: "",
            bankBuildingSocietyName: "",
            branch: "",
            sortCode: "",
            accountNumber: ""
        },
        emergencyInformation: {

            contact1: {

                name: "",
                relationship: "",
                telephoneNumber: ""

            },

            contact2: {

                name: "",
                relationship: "",
                telephoneNumber: ""

            }

        },
        healthAndSafety: {
            isVduUser: "",
            vduLastEyeTestDate: "",
            vduGlassesIssued: ""
        },
        skillsAndCertificates:{
            paymentFrequency: "",
            cscsSkillsCard: "",
            firstAid:"",
            otherTrainingCertificates: "",
            nvq: false,
            pasmaScaffold: false,
            ipaf1b: false,
            ipaf3a3b: false,
            ipafHarness: false,
            ipafPav: false,
            sssts: false,
            abrasiveWheels: false,
            cpcsSkillsCard: false,
            paslode: false,
            manualHandling: false,
            hilti: false,
            other: false
            
        },
        declaration: {
            declarationConfirmed: false
        }

    });



    // --------------------------------------------------------
    // Form Handlers
    // --------------------------------------------------------


    /*
     * Updates HMRC Checklist fields
     */
    const handleSectionChange = (section) => (event) => {

        const {

            name,

            value,

            type,

            checked

        } = event.target;

        setNewStarter(prev => ({

            ...prev,

            [section]: {

                ...prev[section],

                [name]:

                    type === "checkbox"

                        ? checked

                        : value

            }

        }));

    };

    /*
    * Updates file uploads inside sections
    */
    const handleSectionFileChange = (section) => (field, file) => {

        setNewStarter(prev => ({

            ...prev,

            [section]: {

                ...prev[section],

                [field]: file

            }

        }));

    };

    const handleCheckboxChange = (section) => (event) => {
        const { name, checked } = event.target;

        setNewStarter(prev => ({

            ...prev,

            [section]: {

                ...prev[section],

                [name]: checked

            }

        }));

   };

   const handleEmergencyContactChange = (contact) => (event) => {

        const {
            name,
            value
        } = event.target;


        setNewStarter(prev => ({

            ...prev,

            emergencyInformation: {

                ...prev.emergencyInformation,

                [contact]: {

                    ...prev.emergencyInformation[contact],

                    [name]: value

                }

            }

        }));

    };

    

    /*
     * Move to next section
     */
    const handleContinue = () => {

        const validationErrors = validateCurrentStep();

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setCurrentStep(prev => prev + 1);

    };


    const validateCurrentStep = () => {

        switch (sections[currentStep]) {

            case "HMRC_CHECKLIST":
                return validateHMRCChecklist();
            break;
            case "AUTHORITY_TO_WORK":
                return validateAuthorityToWork();
            break;
            case "DRIVING_LICENCE":
                return validateDrivingLicence();
            break;
            case "STUDENT_LOAN":
                return validateStudentLoan();
            break;
            case "HEALTH_AND_SAFETY":
                return validateHealthAndSafety();
            break;
            case "SKILLS_AND_CERTIFICATES":
                return validateSkillsAndCertificates();
                break;
            case "EMERGENCY_CONTACT":
                return validateEmergencyInformation();
            break;
            case "BANK_DETAILS":
                return validateBankDetails();
            break;
            case "DECLARATION":
                return validateDeclaration();
            break;
            

            default:
                return {};
        }

    };



    /*
     * Return to previous section
     */
    const handleBack = () => {

        if (currentStep === 0) {
            setFormStarted(false);
            return;
        }

        setCurrentStep((step) => step - 1);
    };


    /*
    * Upload New Starter Documents
    */

    const uploadNewStarterDocument = async (
        newStarterId,
        file,
        documentType
    ) => {

        if (!file) {
            return;
        }


        const formData = new FormData();


        formData.append(
            "file",
            file
        );


        formData.append(
            "documentType",
            documentType
        );



        await axios.post(

            `/new-starter/${newStarterId}/upload-document`,

            formData,

            {

                headers: {

                    "Content-Type":
                        "multipart/form-data"

                }

            }

        );

    };




    const handleSubmit = async () => {
        if (!turnstileToken) {
            setTurnstileError("Please complete the verification.");
            return;
        }

        if (submitting) {
            return;
        }

        const validationErrors = validateDeclaration();

        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        setSubmitting(true);

        try {

            const payload = {

                applicationId: starterData.applicationId,
                
                jobTitle:starterData.jobTitle,

                paymentFrequency:starterData.paymentFrequency,

                turnstileToken,

                hmrcChecklist: newStarter.hmrcChecklist,

                authorityToWork: newStarter.authorityToWork,

                drivingLicence: newStarter.drivingLicence,

                studentLoan: newStarter.studentLoan,

                healthAndSafety: newStarter.healthAndSafety,

                skillsAndCertificates: newStarter.skillsAndCertificates,

                emergencyInformation:newStarter.emergencyInformation,

                bankDetails: newStarter.bankDetails,

                declaration: newStarter.declaration

            };
            const res = await axiosFetch({

                axiosInstance: axios,

                method: "post",

                url: "/recordNewStarter",

                requestConfig: {

                    data: payload

                }

            });

            const newStarterId =
                        res?.id ||
                        res?.data?.id ||
                        submitResponse?.id ||
                        submitResponse?.data?.id;

            if (!newStarterId) {
                throw new Error("New Starter ID not returned");
            }

            /*
            * Upload Visa
            */

            if (newStarter.authorityToWork.visaDocument) {
                await uploadNewStarterDocument(
                    newStarterId,
                    newStarter.authorityToWork.visaDocument,
                    "VISA"
                );

            }

            /*
            * Upload Vehicle Insurance
            */

            if (newStarter.drivingLicence.vehicleInsuranceDocument) {
                await uploadNewStarterDocument(
                    newStarterId,
                    newStarter.drivingLicence.vehicleInsuranceDocument,
                    "VEHICLE_INSURANCE"
                );
            }

            setSubmitted(true);

        }

        catch (err) {

            console.error("Submission failed", err);

        }

        finally {

            setSubmitting(false);

        }

    };



    // --------------------------------------------------------
    // Section Renderer
    // --------------------------------------------------------

    const renderCurrentSection = () => {


        const currentSection = sections[currentStep];


        switch (currentSection) {


            case "HMRC_CHECKLIST":

                return (
                    <HMRCChecklistForm
                        data={newStarter.hmrcChecklist}
                        errors={errors}
                        handleChange={handleSectionChange("hmrcChecklist")}
                        onBack={handleBack}
                        onContinue={handleContinue}
                    />

                );
            break;


            /*
             * Future Sections
             */

            case "AUTHORITY_TO_WORK":

                return (

                    <AuthorityToWorkForm
                        data={newStarter.authorityToWork}
                        errors={errors}
                        handleChange={handleSectionChange("authorityToWork")}
                        handleFileChange={handleSectionFileChange("authorityToWork")}
                        onBack={handleBack}
                        onContinue={handleContinue}
                    />

                );

            break;
            case "DRIVING_LICENCE":
                return <DrivingLicenceForm
                    data={newStarter.drivingLicence}
                    paymentFrequency={starterData.paymentFrequency}
                    errors={errors}
                    handleChange={handleSectionChange("drivingLicence")}
                    handleFileChange={handleSectionFileChange("drivingLicence")}
                    onBack={handleBack}
                    onContinue={handleContinue}
                />
            break;


            case "STUDENT_LOAN":
                return <StudentLoanForm
                    data={newStarter.studentLoan}
                    errors={errors}
                    handleChange={handleSectionChange("studentLoan")}
                    handleCheckboxChange={handleCheckboxChange("studentLoan")}
                    onBack={handleBack}
                    onContinue={handleContinue}
                />
                break;
            case "SKILLS_AND_CERTIFICATES":
                return (<SkillsAndCertificatesForm
                        data={newStarter.skillsAndCertificates}
                        errors={errors}
                        handleChange={handleSectionChange("skillsAndCertificates")}
                        handleCheckboxChange={handleCheckboxChange("skillsAndCertificates")}
                        onBack={handleBack}
                        onContinue={handleContinue}
                    />
                );
                break;

            case "HEALTH_AND_SAFETY":
                return (
                    <HealthAndSafetyForm
                        data={newStarter.healthAndSafety}
                        errors={errors}
                        handleChange={handleSectionChange("healthAndSafety")}
                        onBack={handleBack}
                        onContinue={handleContinue}
                    />
                );

                break;


            case "EMERGENCY_CONTACT":
                return <EmergencyInformationForm
                    data={newStarter.emergencyInformation}
                    errors={errors}
                    handleChange={handleEmergencyContactChange}
                    onBack={handleBack}
                    onContinue={handleContinue}
                />
                break;


            case "BANK_DETAILS":
                return <BankDetailsForm
                    data={newStarter.bankDetails}
                    errors={errors}
                    handleChange={handleSectionChange("bankDetails")}
                    onBack={handleBack}
                    onContinue={handleContinue}
                />
                break;


            case "DECLARATION":
                return (
                    <DeclarationForm
                        data={newStarter.declaration}
                        errors={errors}
                        handleChange={handleSectionChange("declaration")}
                        onBack={handleBack}
                        onSubmit={handleSubmit}
                        loading={submitting}
                        turnstileToken={turnstileToken}
                        setTurnstileToken={setTurnstileToken}
                        turnstileError={turnstileError}
                        setTurnstileError={setTurnstileError}
                    />
                );
                break;


            default:

                return null;

        }

    };



    // --------------------------------------------------------
    // Load New Starter Data
    // --------------------------------------------------------

    useEffect(() => {
        const code = searchParams.get("code");
        if (!code) {
            setError("This starter link is invalid or has expired.");
            setLoading(false);
            return;
        }
        axios.get(
            "/new-starter",
            {
                params: {
                    code: code
                }
            }
        )
        .then(response => {
            const data = response.data;
            setStarterData(data);
            /*
            * Pre-populate HMRC fields using application data
            * supplied from the job application record
            */
            setNewStarter(prev => ({
                ...prev,
                // ----------------------------------------------------
                // HMRC Checklist pre-population
                // ----------------------------------------------------
                hmrcChecklist: {
                    ...prev.hmrcChecklist,
                    firstName: data.firstName || "",
                    lastName: data.lastName || "",
                    employmentStartDate:data.action.startDate
                },

                skillsAndCertificates:{
                    ...prev.skillsAndCertificates,
                    paymentFrequency:data.paymentFrequency
                }
            }));
        })
        .catch(error => {
            console.error(error);
            setError("This starter link is invalid or has expired.");
        })
        .finally(() => {
            setLoading(false);
        });
    }, [searchParams]);
    // --------------------------------------------------------
    // Check Link Status
    // --------------------------------------------------------
    useEffect(() => {
        // Only poll while the user is completing the form
        if (!formStarted) {
            return;
        }
        const code = searchParams.get("code");
        const interval = setInterval(async () => {
            try {
                const response = await axios.get("/new-starter", {
                    params: {
                        code
                    }
                });
                if (response.data.completed) {
                    // Update local state
                    setStarterData(prev => ({
                        ...prev,
                        completed: true
                    }));
                    // Return to the welcome page
                    setFormStarted(false);
                    setCurrentStep(0);
                }
            } catch (err) {
                console.error("Polling failed", err);
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [formStarted, searchParams]);

    // --------------------------------------------------------
    // Loading State
    // --------------------------------------------------------

    if (loading) {


        return (

            <Box

                sx={{

                    display: "flex",

                    justifyContent: "center",

                    alignItems: "center",

                    minHeight: "60vh"

                }}

            >

                <CircularProgress />

            </Box>

        );

    }

    const startForm = () => {

        if (starterData.completed) {
            return;
        }
        scrollToTop();
        setFormStarted(true);
    };


    // --------------------------------------------------------
    // Error State
    // --------------------------------------------------------

    if (error) {


        return (

            <Box

                sx={{

                    maxWidth:900,

                    mx:"auto",

                    mt:8,

                    textAlign:"center"

                }}

            >

                <Typography
                    variant="h3"
                    gutterBottom
                >

                    Page Not Found

                </Typography>



                <Typography sx={{mb:4}}>

                    {error}

                </Typography>



                <Button

                    variant="contained"

                    onClick={() => navigate("/")}

                >

                    Return Home

                </Button>


            </Box>

        );

    }

    if (submitted) {
        return (

            <SubmissionSuccess
                onReturn={() => {
                    setSubmitted(false);
                    setFormStarted(false);
                    setCurrentStep(0);
                    setSubmissionSuccessReturn(true);
                }}
            />

        );

    }

    // --------------------------------------------------------
    // Page Render
    // --------------------------------------------------------

    if (!formStarted) {
        return (
            <WelcomeSection
                starterData={starterData}
                completed={starterData.completed}
                submissionSuccessReturn={submissionSuccessReturn}
                onStart={() => startForm()}
            />
        );
    }

    return (
        <FormSection starterData={starterData}
                    currentStep={currentStep}
                    steps={steps}>
            {renderCurrentSection()}
        </FormSection>
    );


}